import React from "react";
import {
  Loader,
  LoaderSizes,
  Button,
  Iconography,
  IconographySizes,
  IconographyThemes,
  Pagination,
  Avatar,
  AvatarSize,
  Logo,
  LogoThemes,
  InlineStatus,
  InlineStatusAppearance,
  InlineStatusIconType,
  Notification,
  NotificationVariants,
  SearchInput,
  Card,
  TextArea,
  Modal,
} from "@hid-galaxy-ui/galaxy-react";
import { useNavigate } from "react-router";
import moment from "moment";
import axios from "axios";
import { axiosRestInstance } from "../../../../../common/axios";
 
import { ReactComponent as NoUserStateSVG } from "../../../../../common/assets/icons/emptystateuser.svg";
 
import "./FeatureList.scss";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { useIsHidStaff } from "../../../../../hooks/useIsHidStaff";
import {
  getUserListHIDSagaAction,
  sendAccountInviteSagaAction,
} from "../../../../../redux/saga/user/saga-actions";
import { UserListType } from "../../../../../redux/slices/user/Types";
import DeleteModal from "../../../../../common/components/DeleteModal";
import HIDUserFormModal from "../../hid-users/hid-user-form-modal";
 
const FeatureList = () => {
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(16);
  const [addModalOpen, setAddModalOpen] = React.useState<boolean>(false);
  const [updateModalOpen, setUpdateModalOpen] = React.useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(false);
  const [selectedUser, setSelectedUser] = React.useState<any | null>(null);
  const [userToDelete, setUserToDelete] = React.useState<any | null>(null);
  const [toasts, setToasts] = React.useState([]);
  const [userListHID, setUserListHID] = React.useState([]);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [activeTab, setActiveTab] = React.useState<"latest" | "trending">(
    "latest"
  );
  const [votedFeatureIds, setVotedFeatureIds] = React.useState<Set<string>>(
    new Set()
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const isFetchingRef = React.useRef(false);
 
  const { status } = useAppSelector((state) => state.user);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { allPartners } = useAppSelector((state) => state.partner_details);
  const isHidStaff = useIsHidStaff();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
 
  React.useEffect(() => {
    // Load data from GET API
    const fetchFeatures = async () => {
      // Prevent multiple simultaneous calls
      if (isFetchingRef.current) {
        console.log("Already fetching, skipping...");
        return;
      }
 
      isFetchingRef.current = true;
      setIsLoading(true);
 
      try {
        // Use allPartners from Redux instead of making API call
        const partnerMap = new Map<string, string>();
        if (allPartners && allPartners.length > 0) {
          console.log("Using allPartners from Redux:", allPartners.length);
          // Create a map of partnerID -> partnerName
          allPartners.forEach((partner: any) => {
            if (partner.ID && partner.partner_name) {
              partnerMap.set(partner.ID.toString(), partner.partner_name);
            }
          });
          console.log("Partner Map created:", Object.fromEntries(partnerMap));
        } else {
          console.log("No partners available in Redux state");
        }
 
        const response = await axios.get(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-supported`
        );
        console.log("API Response:", response.data);
 
        // Get all features the user has already voted for
        const partnerID = loggedUserDetail?.ID?.toString() || "2";
        try {
          const voteStatusResponse = await axios.get(
            `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-votes?partnerID=${partnerID}`
          );
          console.log("Vote Status Response:", voteStatusResponse.data);
 
          // Get all feature IDs that user has voted for
          if (voteStatusResponse.data && voteStatusResponse.data.data) {
            const userVotes = Array.isArray(voteStatusResponse.data.data)
              ? voteStatusResponse.data.data
              : [];
 
            // Create a set of all feature IDs user has voted for
            const votedIds = new Set<string>();
            userVotes.forEach((vote: any) => {
              if (vote.voteStatus === true || vote.voteStatus === 1) {
                votedIds.add(vote.featureID?.toString() || "");
              }
            });
 
            setVotedFeatureIds(votedIds);
            console.log("User has voted for features:", Array.from(votedIds));
          }
        } catch (voteError) {
          console.error("Error fetching vote status:", voteError);
          // Continue even if vote status check fails
        }
 
        if (response.data && response.data.data) {
          const apiData = Array.isArray(response.data.data)
            ? response.data.data
            : [];
 
          // Fetch comments for all features
          const featuresWithComments = await Promise.all(
            apiData.map(async (item: any) => {
              // Get partner name from the partnerMap using partnerID
              const partnerName =
                item.partnerName ||
                (item.partnerID
                  ? partnerMap.get(item.partnerID.toString())
                  : null) ||
                "Unknown";
 
              try {
                const apiUrl = `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-comments?featureID=${item.friID}`;
                console.log(`Fetching comments from: ${apiUrl}`);
 
                const commentsResponse = await axios.get(apiUrl);
 
                const commentsData = commentsResponse.data?.data || [];
 
                // Verify comments match the feature ID
                if (Array.isArray(commentsData) && commentsData.length > 0) {
                  console.log(
                    `Sample comment featureIDs for ${item.friID}:`,
                    commentsData.map((c: any) => c.featureID)
                  );
                }
 
                // Map comments to expected format
                const comments = Array.isArray(commentsData)
                  ? commentsData
                      .filter((c: any) => {
                        // Client-side filtering: Only include comments that match THIS feature's ID
                        const commentFeatureID = c.featureID
                          ? c.featureID.toString().trim()
                          : "";
                        const currentFeatureID = item.friID
                          ? item.friID.toString().trim()
                          : "";
                        const matches = commentFeatureID === currentFeatureID;
 
                        if (!matches && commentFeatureID) {
                          console.log(
                            ` Filtering out comment: featureID "${commentFeatureID}" doesn't match "${currentFeatureID}"`
                          );
                        }
 
                        return matches;
                      })
                      .map((c: any) => {
                        // Check if comment's partnerID matches logged-in user's ID
                        let userName =
                          c.userName ||
                          c.user ||
                          c.submittedby ||
                          "Unknown User";
 
                        if (loggedUserDetail && c.partnerID) {
                          const commentPartnerID = c.partnerID.toString();
                          const loggedUserID = loggedUserDetail.ID?.toString();
 
                          if (commentPartnerID === loggedUserID) {
                            userName =
                              `${loggedUserDetail.first_name || ""} ${
                                loggedUserDetail.last_name || ""
                              }`.trim() ||
                              loggedUserDetail.email ||
                              "You";
                          }
                        }
 
                        return {
                          commentID: c.commentID,
                          text: c.comment || c.text,
                          date: c.createdAt || c.createdDate || c.date,
                          user: userName,
                          partnerID: c.partnerID,
                          featureID: c.featureID,
                        };
                      })
                      .sort((a: any, b: any) => {
                        // Sort by date descending - newest first
                        const dateA = new Date(a.date).getTime();
                        const dateB = new Date(b.date).getTime();
                        return dateB - dateA;
                      })
                  : [];
 
                return {
                  ...item,
                  id: item.friID,
                  createdBy: item.submittedby,
                  date: item.submittedDate,
                  votes: item.upVoteCount || 0,
                  commentsCount: comments.length,
                  comments: comments,
                  partnerName: partnerName || "Unknown",
                  partnerID: item.partnerID,
                };
              } catch (commentError) {
                console.error(
                  `Error fetching comments for feature ${item.friID}:`,
                  commentError
                );
                return {
                  ...item,
                  id: item.friID,
                  createdBy: item.submittedby,
                  date: item.submittedDate,
                  votes: item.upVoteCount || 0,
                  commentsCount: 0,
                  comments: [],
                  partnerName: partnerName || "Unknown",
                  partnerID: item.partnerID,
                };
              }
            })
          );
 
          console.log("Features loaded with comments:", featuresWithComments);
          setUserListHID(featuresWithComments);
        }
      } catch (error) {
        console.error("Error fetching features:", error);
        setUserListHID([]);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
        console.log("=== fetchFeatures completed ===");
      }
    };
 
    // Only fetch if loggedUserDetail is available
    if (loggedUserDetail?.ID) {
      fetchFeatures();
    } else {
      console.log("Skipping fetch - loggedUserDetail not ready yet");
    }
  }, [refreshKey, loggedUserDetail?.ID, dispatch]);
 
  // Listen for vote updates and reload data from API
  React.useEffect(() => {
    const handleVoteUpdate = () => {
      setRefreshKey((prev) => prev + 1);
    };
 
    window.addEventListener("voteUpdated", handleVoteUpdate);
    return () => {
      window.removeEventListener("voteUpdated", handleVoteUpdate);
    };
  }, []);
 
  // Reset to first page when search term changes
  React.useEffect(() => {
    setCurrentPageNumber(1);
  }, [searchTerm]);
 
  // Handle send account invite notifications
  // React.useEffect(() => {
  //   if (
  //     status.sendAccountInviteStatus !== "idle" &&
  //     status.sendAccountInviteMessage
  //   ) {
  //     const isSuccess = status.sendAccountInviteStatus === "success";
  //     const content = {
  //       title: isSuccess ? "Account Invite Sent" : "Error",
  //       message: status.sendAccountInviteMessage,
  //       variant: isSuccess
  //         ? NotificationVariants.Success
  //         : NotificationVariants.Error,
  //       icon: (
  //         <Iconography
  //           icon={isSuccess ? "check" : "ban"}
  //           size={IconographySizes.Medium}
  //         />
  //       ),
  //       expanded: true,
  //     };
  //     setToasts([content]);
 
  //     // Reset the status after showing notification
  //     dispatch(userSliceAction.resetSendAccountInviteStatus());
  //   }
  // }, [
  //   status.sendAccountInviteStatus,
  //   status.sendAccountInviteMessage,
  //   dispatch,
  // ]);
 
  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };
 
  const onPageSizesClick = (e: any, value: any) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };
 
  const filteredUsers = userListHID.filter((user) => {
    const matchesSearch =
      searchTerm.length === 0 ||
      (user.title &&
        user.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.description &&
        user.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.slackNumber &&
        user.slackNumber.toLowerCase().includes(searchTerm.toLowerCase()));
 
    return matchesSearch;
  });
 
  const sortedUsers = React.useMemo(() => {
    const usersCopy = [...filteredUsers];
    if (activeTab === "trending") {
      return usersCopy.sort(
        (a: any, b: any) => (b.votes || 0) - (a.votes || 0)
      );
    }
 
    // latest: sort by date (newest first), fallback to original order
    return usersCopy.sort((a: any, b: any) => {
      const aTime = a.date ? new Date(a.date).getTime() : 0;
      const bTime = b.date ? new Date(b.date).getTime() : 0;
      return bTime - aTime;
    });
  }, [filteredUsers, activeTab]);
 
  const totalRecords = sortedUsers.length;
 
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedUsers.slice(startIndex, endIndex);
  }, [sortedUsers, currentPageNumber, pageSize]);
 
  const onAddUserClick = () => {
    setUpdateModalOpen(false);
    setAddModalOpen(true);
  };
 
  const onEditUserClick = (user: any) => {
    setSelectedUser(user);
    setAddModalOpen(false);
    setUpdateModalOpen(true);
  };
 
  const onDeleteUserClick = (user: any) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };
 
  const handleDeleteUser = () => {
    if (userToDelete) {
      // TODO: Call DELETE API here
      // For now, just trigger refresh to reload from API
      setDeleteModalOpen(false);
      setRefreshKey((prev) => prev + 1);
    }
  };
 
  const handleSendAccountInvite = (user: any) => {
    dispatch(
      sendAccountInviteSagaAction({
        ID: user.ID,
        userEmail: user.email,
      })
    );
  };
 
  const userStatus = (status: string) => {
    if (status === "Enable") {
      return (
        <InlineStatus
          label="Enabled"
          appearance={InlineStatusAppearance.Positive}
          iconType={InlineStatusIconType.Dot}
        />
      );
    } else if (status === "Invited") {
      return (
        <InlineStatus
          label={status}
          appearance={InlineStatusAppearance.Caution}
          iconType={InlineStatusIconType.Dot}
        />
      );
    } else {
      return (
        <InlineStatus
          label={status === "Disable" ? "Disabled" : status}
          appearance={InlineStatusAppearance.Danger}
          iconType={InlineStatusIconType.Dot}
        />
      );
    }
  };
 
  const UserCard: React.FC<{ user: any }> = ({ user }) => {
    const [votes, setVotes] = React.useState(user.votes || 0);
    const [showCommentModal, setShowCommentModal] = React.useState(false);
    const [showDetailModal, setShowDetailModal] = React.useState(false);
    const [showDeleteCommentModal, setShowDeleteCommentModal] =
      React.useState(false);
    const [showEditSuccessModal, setShowEditSuccessModal] =
      React.useState(false);
    const [showErrorModal, setShowErrorModal] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [commentToDeleteIndex, setCommentToDeleteIndex] = React.useState<
      number | null
    >(null);
    const [comment, setComment] = React.useState("");
    const [comments, setComments] = React.useState(user.comments || []);
    const [commentsCount, setCommentsCount] = React.useState(
      user.commentsCount || 0
    );
    const [hasVoted, setHasVoted] = React.useState(false);
    const [loadingComments, setLoadingComments] = React.useState(false);
    const [votingInProgress, setVotingInProgress] = React.useState(false);
    const [addingComment, setAddingComment] = React.useState(false);
    const [editingCommentIndex, setEditingCommentIndex] = React.useState<
      number | null
    >(null);
    const [editedCommentText, setEditedCommentText] = React.useState("");
    const [deletingCommentIndex, setDeletingCommentIndex] = React.useState<
      number | null
    >(null);
 
    // Check if user has already voted for THIS specific feature
    const hasVotedForThisFeature = votedFeatureIds.has(
      user.friID?.toString() || ""
    );
 
    // Sync votes and comments when user data changes
    React.useEffect(() => {
      setVotes(user.votes || 0);
      setComments(user.comments || []);
      setCommentsCount(user.commentsCount || 0);
    }, [user.votes, user.comments, user.commentsCount, user.friID]);
 
    const handleVote = async () => {
      // Check if user has already voted for THIS feature
      if (hasVotedForThisFeature) {
        setErrorMessage("You have already voted for this feature.");
        setShowErrorModal(true);
        return;
      }
 
      if (votingInProgress) return;
 
      setVotingInProgress(true);
 
      // Increment vote count optimistically
      const newVotes = votes + 1;
      setVotes(newVotes);
      setHasVoted(true);
 
      try {
        // Call API to update votes in backend
        const partnerID = loggedUserDetail?.ID?.toString() || "2";
        await axios.post(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-votes`,
          {
            partnerID: partnerID,
            featureID: user.friID,
            voteStatus: true,
          }
        );
        console.log("Vote added successfully");
 
        // Add this feature to voted features set
        setVotedFeatureIds((prev) =>
          new Set(prev).add(user.friID?.toString() || "")
        );
 
        // Trigger full page refresh to update all vote counts
        setTimeout(() => {
          window.dispatchEvent(new Event("voteUpdated"));
        }, 100);
      } catch (error) {
        console.error("Error adding vote:", error);
        setErrorMessage("Failed to add vote. Please try again.");
        setShowErrorModal(true);
        // Revert vote count on error
        setVotes(votes);
        setHasVoted(false);
      } finally {
        setVotingInProgress(false);
      }
    };
 
    const handleAddComment = async () => {
      if (comment.trim()) {
        console.log("Starting to add comment, setting addingComment to true");
        setAddingComment(true);
 
        // Small delay to ensure state updates and loader shows
        await new Promise((resolve) => setTimeout(resolve, 100));
 
        try {
          // Call API to save comment in backend
          const partnerID = loggedUserDetail?.ID?.toString() || "2";
          const response = await axios.post(
            `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-comments`,
            {
              comment: comment.trim(),
              featureID: user.friID,
              partnerID: partnerID,
            }
          );
          console.log("Comment added successfully for feature:", user.friID);
 
          // Clear the comment input
          setComment("");
 
          // Fetch updated comments from API to get the complete comment data including commentID
          console.log("Fetching updated comments for feature:", user.friID);
          const commentsResponse = await axios.get(
            `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-comments?featureID=${user.friID}`
          );
 
          const commentsData = commentsResponse.data?.data || [];
          console.log("Fetched comments after adding:", commentsData);
 
          // Map comments to expected format
          const updatedComments = Array.isArray(commentsData)
            ? commentsData
                .filter((c: any) => {
                  const commentFeatureID = c.featureID
                    ? c.featureID.toString().trim()
                    : "";
                  const currentFeatureID = user.friID
                    ? user.friID.toString().trim()
                    : "";
                  return commentFeatureID === currentFeatureID;
                })
                .map((c: any) => {
                  // Check if comment's partnerID matches logged-in user's ID
                  let userName =
                    c.userName || c.user || c.submittedby || "Unknown User";
 
                  if (loggedUserDetail && c.partnerID) {
                    const commentPartnerID = c.partnerID.toString();
                    const loggedUserID = loggedUserDetail.ID?.toString();
 
                    if (commentPartnerID === loggedUserID) {
                      userName =
                        `${loggedUserDetail.first_name || ""} ${
                          loggedUserDetail.last_name || ""
                        }`.trim() ||
                        loggedUserDetail.email ||
                        "You";
                    }
                  }
 
                  return {
                    commentID: c.commentID,
                    text: c.comment || c.text,
                    date: c.createdAt || c.createdDate || c.date,
                    user: userName,
                    partnerID: c.partnerID,
                    featureID: c.featureID,
                  };
                })
                .sort((a: any, b: any) => {
                  // Sort by date descending - newest first
                  const dateA = new Date(a.date).getTime();
                  const dateB = new Date(b.date).getTime();
                  return dateB - dateA;
                })
            : [];
 
          console.log("Updated comments array:", updatedComments);
 
          // Update local state with fresh data from API
          setComments(updatedComments);
          setCommentsCount(updatedComments.length);
 
          // Small delay before closing modal to show success state
          await new Promise((resolve) => setTimeout(resolve, 300));
          setShowCommentModal(false);
 
          // If detail modal is NOT open, trigger full page refresh
          // If detail modal IS open, we've already updated the local state with API data
          if (!showDetailModal) {
            // Trigger full page refresh for list view updates
            window.dispatchEvent(new Event("voteUpdated"));
          }
        } catch (error) {
          console.error("Error adding comment:", error);
          setErrorMessage("Failed to add comment. Please try again.");
          setShowErrorModal(true);
        } finally {
          console.log(
            "Finished adding comment, setting addingComment to false"
          );
          setAddingComment(false);
        }
      }
    };
 
    const handleDeleteComment = (commentIndex: number) => {
      const commentToDelete = comments[commentIndex];
      const currentUserID = loggedUserDetail?.ID?.toString();
 
      // Check if user is the owner of the comment
      if (commentToDelete.partnerID?.toString() !== currentUserID) {
        setErrorMessage("You can only delete your own comments.");
        setShowErrorModal(true);
        return;
      }
 
      // Show confirmation modal
      setCommentToDeleteIndex(commentIndex);
      setShowDeleteCommentModal(true);
    };
 
    const confirmDeleteComment = async () => {
      if (commentToDeleteIndex === null) return;
 
      const commentIndex = commentToDeleteIndex;
      const commentToDelete = comments[commentIndex];
 
      setDeletingCommentIndex(commentIndex);
      setShowDeleteCommentModal(false);
 
      try {
        // Call API to delete comment using commentID
        const commentID = commentToDelete.commentID;
        if (!commentID) {
          setErrorMessage("Comment ID not found. Cannot delete comment.");
          setShowErrorModal(true);
          return;
        }
 
        await axios.delete(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-comments/${commentID}`
        );
        console.log("Comment deleted successfully");
 
        // Remove comment from local state
        setComments((prev: any) =>
          prev.filter((_: any, idx: number) => idx !== commentIndex)
        );
        setCommentsCount((prev: number) => prev - 1);
 
        // Trigger refresh if not in detail modal
        if (!showDetailModal) {
          window.dispatchEvent(new Event("voteUpdated"));
        }
      } catch (error) {
        console.error("Error deleting comment:", error);
        setErrorMessage("Failed to delete comment. Please try again.");
        setShowErrorModal(true);
      } finally {
        setDeletingCommentIndex(null);
        setCommentToDeleteIndex(null);
      }
    };
 
    const handleEditComment = async (commentIndex: number) => {
      if (!editedCommentText.trim()) {
        setErrorMessage("Comment cannot be empty.");
        setShowErrorModal(true);
        return;
      }
 
      const commentToEdit = comments[commentIndex];
      console.log("Editing comment:", commentToEdit);
      console.log("All comments:", comments);
 
      const currentUserID = loggedUserDetail?.ID?.toString();
 
      // Check if user is the owner of the comment
      if (commentToEdit.partnerID?.toString() !== currentUserID) {
        setErrorMessage("You can only edit your own comments.");
        setShowErrorModal(true);
        return;
      }
 
      try {
        // Call API to update comment using commentID in endpoint
        const commentID = commentToEdit.commentID;
        console.log("Comment ID for edit:", commentID);
 
        if (!commentID) {
          console.error("Comment object missing commentID:", commentToEdit);
          console.error("Comment ID not found. Cannot edit comment.");
          return;
        }
 
        await axios.put(
          `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/feature-comments/${commentID}`,
          {
            comment: editedCommentText.trim(),
            featureID: user.friID,
            partnerID: currentUserID,
          }
        );
        console.log("Comment updated successfully");
 
        // Update comment in local state
        setComments((prev: any) =>
          prev.map((c: any, idx: number) =>
            idx === commentIndex ? { ...c, text: editedCommentText.trim() } : c
          )
        );
 
        // Reset edit state
        setEditingCommentIndex(null);
        setEditedCommentText("");
 
        // Show success modal (user must close it manually)
        setShowEditSuccessModal(true);
 
        // Trigger refresh if not in detail modal
        if (!showDetailModal) {
          window.dispatchEvent(new Event("voteUpdated"));
        }
      } catch (error) {
        console.error("Error updating comment:", error);
        setErrorMessage("Failed to update comment. Please try again.");
        setShowErrorModal(true);
      }
    };
 
    const startEditingComment = (commentIndex: number) => {
      setEditingCommentIndex(commentIndex);
      setEditedCommentText(comments[commentIndex].text);
    };
 
    const cancelEditingComment = () => {
      setEditingCommentIndex(null);
      setEditedCommentText("");
    };
 
    const formattedDate = user.date
      ? moment(user.date).format("DD MMM YYYY")
      : "";
 
 
 return (
      <>
        <div className="feature-card" onClick={() => setShowDetailModal(true)}>
          <div className="card-header">
            <div className="title-section">
              <h3 className="feature-title">{user.title || "Untitled"}</h3>
              <Button
                variant="secondary"
                icon={
                  <Iconography
                    icon="userPen"
                    size={IconographySizes.Medium}
                    theme={IconographyThemes.Info}
                  />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onEditUserClick(user);
                }}
              />
              {/* <span className="status-badge">LIVE IDEA</span> */}
            </div>
            <div className="meta-info">
              <span className="created-by">
                Submitted by : <strong>{user.createdBy || "Unknown"}</strong>
              </span>
            </div>
            <div className="meta-info">
              <span className="created-by">
                Partner Name : <strong>{user.partnerName || "Unknown"}</strong>
              </span>
            </div>
          </div>
 
          <div className="card-body">
            {user.description && (
              <>
                <p className="description">{user.description}</p>
              </>
            )}
            <div className="body-meta-row">
              {formattedDate && (
                <span className="date-info">{formattedDate}</span>
              )}
              <div className="meta-right">
                <div className="meta-pill votes-pill">
                  <Iconography icon="thumbsUp" size={IconographySizes.Small} />
                  <span>{votes}</span>
                </div>
                <div className="meta-pill comments-pill">
                  <Iconography icon="memoPad" size={IconographySizes.Small} />
                  <span>{commentsCount}</span>
                </div>
              </div>
            </div>
          </div>
 
          <div className="card-footer">
            <div className="vote-section">
              <Button
                variant={
                  hasVoted || hasVotedForThisFeature ? "secondary" : "primary"
                }
                label={hasVoted || hasVotedForThisFeature ? "Voted" : "Vote"}
                disabled={hasVoted || hasVotedForThisFeature}
                onClick={(e) => {
                  e.stopPropagation();
                  handleVote();
                }}
                icon={
                  <Iconography
                    icon={
                      hasVoted || hasVotedForThisFeature ? "check" : "thumbsUp"
                    }
                    size={IconographySizes.Small}
                  />
                }
              />
            </div>
            <div className="action-buttons">
              <Button
                variant="secondary"
                icon={
                  <Iconography
                    icon="memoPad"
                    size={IconographySizes.Medium}
                    theme={IconographyThemes.Info}
                  />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCommentModal(true);
                }}
              />
              {/* {user.status === 'Completed' && (
                <>
                  <Button
                    variant="secondary"
                    icon={
                      <Iconography
                        icon="userPen"
                        size={IconographySizes.Medium}
                        theme={IconographyThemes.Info}
                      />
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditUserClick(user);
                    }}
                  />
                  <Button
                    variant="secondary"
                    icon={
                      <Iconography
                        icon="trashCan"
                        size={IconographySizes.Medium}
                        theme={IconographyThemes.Error}
                      />
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteUserClick(user);
                    }}
                  />
                </>
              )} */}
            </div>
          </div>
        </div>
 
        {showDetailModal && (
          <Modal
            headerText="Feature Details"
            width={620}
            onClose={() => setShowDetailModal(false)}
          >
            <div className="detail-modal-content">
              <div className="detail-header">
                <div className="title-with-badge">
                  <h2>{user.title || "Untitled"}</h2>
                  {/* <span className="status-badge-modal">LIVE IDEA</span> */}
                </div>
                <div className="submitted-info">
                  <span>
                    Submitted by :{" "}
                    <strong>{user.createdBy || "Unknown"}</strong>
                  </span>
                  <span className="slack">
                    {" "}
                    • Partner Name :{" "}
                    <strong>{user.partnerName || "Unknown"}</strong>
                  </span>
                  {formattedDate && (
                    <span className="date"> • {formattedDate}</span>
                  )}
                  {user.category && (
                    <span className="slack">
                      {" "}
                      • Category: <strong>{user.category}</strong>
                    </span>
                  )}
                </div>
              </div>
 
              <div className="detail-body">
                <div className="description-section">
                  <h4>Description</h4>
                  <p>{user.description || "No description provided"}</p>
                </div>
 
                <div className="votes-section">
                  <div className="votes-display">
                    <Iconography
                      icon="thumbsUp"
                      size={IconographySizes.Medium}
                      theme={IconographyThemes.Success}
                    />
                    <div className="vote-info">
                      <span className="vote-count-large">{votes}</span>
                      <span className="vote-text">Total Votes</span>
                    </div>
                  </div>
                  <Button
                    variant={
                      hasVoted || hasVotedForThisFeature
                        ? "secondary"
                        : "primary"
                    }
                    label={
                      votingInProgress
                        ? "Voting..."
                        : hasVoted || hasVotedForThisFeature
                        ? "You Voted"
                        : "Vote for this idea"
                    }
                    disabled={
                      hasVoted || hasVotedForThisFeature || votingInProgress
                    }
                    onClick={handleVote}
                    icon={
                      votingInProgress ? (
                        <Loader variant={LoaderSizes.small} />
                      ) : (
                        <Iconography
                          icon={
                            hasVoted || hasVotedForThisFeature
                              ? "check"
                              : "thumbsUp"
                          }
                          size={IconographySizes.Medium}
                        />
                      )
                    }
                  />
                </div>
 
                <div className="comments-section">
                  <div className="comments-header">
                    <h4>Comments ({comments.length})</h4>
                    <Button
                      variant="primary"
                      label="Add Comment"
                      size="small"
                      onClick={() => setShowCommentModal(true)}
                      icon={
                        <Iconography
                          icon="plus"
                          size={IconographySizes.Small}
                        />
                      }
                    />
                  </div>
 
                  {addingComment && (
                    <div className="adding-comment-loader">
                      <div className="loader-content">
                        <div className="spinner" />
                        <span className="loader-text">
                          Adding your comment...
                        </span>
                      </div>
                    </div>
                  )}
 
                  {comments.length > 0 ? (
                    <div className="comments-list">
                      {comments.map((c: any, idx: number) => {
                        const isOwnComment =
                          c.partnerID?.toString() ===
                          loggedUserDetail?.ID?.toString();
                        const isEditing = editingCommentIndex === idx;
                        const isDeleting = deletingCommentIndex === idx;
 
                        return (
                          <div key={idx} className="comment-box">
                            <div className="comment-header-item">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flex: 1,
                                }}
                              >
                                <strong>{c.user}</strong>
                                <span className="comment-date-item">
                                  {moment(c.date).format("DD MMM YYYY, HH:mm")}
                                </span>
                              </div>
                              {isOwnComment && !isEditing && (
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <Button
                                    variant="secondary"
                                    size="small"
                                    icon={
                                      <Iconography
                                        icon="pen"
                                        size={IconographySizes.Medium}
                                        theme={IconographyThemes.Info}
                                      />
                                    }
                                    onClick={() => startEditingComment(idx)}
                                    disabled={isDeleting}
                                  />
                                  <Button
                                    variant="secondary"
                                    size="small"
                                    icon={
                                      isDeleting ? (
                                        <Loader variant={LoaderSizes.small} />
                                      ) : (
                                        <Iconography
                                          icon="trashCan"
                                          size={IconographySizes.Medium}
                                          theme={IconographyThemes.Error}
                                        />
                                      )
                                    }
                                    onClick={() => handleDeleteComment(idx)}
                                    disabled={isDeleting}
                                  />
                                </div>
                              )}
                            </div>
                            {isEditing ? (
                              <div style={{ marginTop: "12px" }}>
                                <TextArea
                                  label=""
                                  id={`edit-comment-${idx}`}
                                  placeholder="Edit your comment..."
                                  name={`edit-comment-${idx}`}
                                  maxLength={500}
                                  theme="dark"
                                  value={editedCommentText}
                                  onChange={(e: any) =>
                                    setEditedCommentText(e.target.value)
                                  }
                                />
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                    marginTop: "12px",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <Button
                                    variant="secondary"
                                    label="Cancel"
                                    size="small"
                                    onClick={cancelEditingComment}
                                  />
                                  <Button
                                    variant="primary"
                                    label="Save"
                                    size="small"
                                    onClick={() => handleEditComment(idx)}
                                    disabled={!editedCommentText.trim()}
                                  />
                                </div>
                              </div>
                            ) : (
                              <p className="comment-text">{c.text}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="no-comments">
                      <Iconography
                        icon="memoPad"
                        size={IconographySizes.Large}
                      />
                      <p>No comments yet. Be the first to comment!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Modal>
        )}
 
        {showCommentModal && (
          <Modal
            headerText="Add Comment"
            width={500}
            onClose={() => setShowCommentModal(false)}
          >
            <div
              className="comment-modal-content"
              style={{ position: "relative", minHeight: "250px" }}
            >
              {addingComment && showDetailModal && (
                <div className="comment-modal-overlay">
                  <div className="spinner-large" />
                  <p className="overlay-text">Adding your comment...</p>
                </div>
              )}
              <TextArea
                label="Your Comment"
                id="comment"
                placeholder="Share your thoughts..."
                name="comment"
                maxLength={500}
                theme="dark"
                value={comment}
                onChange={(e: any) => setComment(e.target.value)}
              />
              <div className="modal-buttons">
                <Button
                  variant="secondary"
                  label="Cancel"
                  onClick={() => setShowCommentModal(false)}
                  disabled={addingComment}
                />
                <Button
                  variant="primary"
                  label={addingComment ? "Adding..." : "Add Comment"}
                  onClick={handleAddComment}
                  disabled={!comment.trim() || addingComment}
                  icon={
                    addingComment ? (
                      <Loader variant={LoaderSizes.small} />
                    ) : undefined
                  }
                />
              </div>
            </div>
          </Modal>
        )}
        {showDeleteCommentModal && (
          <Modal
            headerText="Delete Comment"
            width={450}
            onClose={() => {
              setShowDeleteCommentModal(false);
              setCommentToDeleteIndex(null);
            }}
          >
            <div
              className="delete-comment-modal-content"
              style={{ textAlign: "center", padding: "20px 0" }}
            >
              <div style={{ marginBottom: "20px" }}>
                <Iconography
                  icon="triangleExclamation"
                  size={IconographySizes.XXLarge}
                  theme={IconographyThemes.Error}
                />
              </div>
              <h3
                style={{
                  marginBottom: "12px",
                  fontSize: "18px",
                  fontWeight: 600,
                }}
              >
                Are you sure you want to delete this comment?
              </h3>
              <p
                style={{
                  marginBottom: "30px",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                This action cannot be undone.
              </p>
              <div
                className="modal-buttons"
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="secondary"
                  label="No, Cancel"
                  onClick={() => {
                    setShowDeleteCommentModal(false);
                    setCommentToDeleteIndex(null);
                  }}
                />
                <Button
                  variant="primary"
                  label="Yes, Delete"
                  onClick={confirmDeleteComment}
                  disabled={deletingCommentIndex !== null}
                  icon={
                    deletingCommentIndex !== null ? (
                      <Loader variant={LoaderSizes.small} />
                    ) : undefined
                  }
                />
              </div>
            </div>
          </Modal>
        )}
        {showEditSuccessModal && (
          <Modal width={450} onClose={() => setShowEditSuccessModal(false)}>
            <div style={{ textAlign: "center", padding: "60px 40px" }}>
              <div
                style={{
                  marginBottom: "30px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    border: "4px solid #7CB342",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "white",
                  }}
                >
                  <Iconography
                    icon="check"
                    size={IconographySizes.XXLarge}
                    theme={IconographyThemes.Success}
                  />
                </div>
              </div>
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "#333",
                  lineHeight: "1.4",
                }}
              >
                Comment Updated
                <br />
                Successfully
              </h3>
            </div>
          </Modal>
        )}
        {showErrorModal && (
          <Modal width={450} onClose={() => setShowErrorModal(false)}>
            <div style={{ textAlign: "center", padding: "60px 40px" }}>
              <div
                style={{
                  marginBottom: "30px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    border: "4px solid #dc2626",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "white",
                  }}
                >
                  <Iconography
                    icon="circleXmark"
                    size={IconographySizes.XXLarge}
                    theme={IconographyThemes.Error}
                  />
                </div>
              </div>
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "#333",
                  lineHeight: "1.4",
                  marginBottom: "12px",
                }}
              >
                Error
              </h3>
              <p
                style={{
                  fontSize: "16px",
                  color: "#666",
                  lineHeight: "1.5",
                }}
              >
                {errorMessage}
              </p>
            </div>
          </Modal>
        )}
      </>
    );
  };
  return (
    <div className="hid-users-page-container feature-list-page">
      <Notification toasts={toasts} setToasts={setToasts} />
 
      <div className="header-container">
        {!isHidStaff && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <h1 className="page-title" style={{ margin: 0 }}>
              Feature Management
            </h1>
            <Button
              variant="primary"
              label="Back To Dashboard"
              onClick={() =>
                navigate(
                  `/partner/${loggedUserDetail?.ID?.toString()}/dashboard/home`
                )
              }
            />
          </div>
        )}
        <div className="tiles-container">
          <div className="incident-stats-tile">
            <div className="tile-content">
              <div className="tile-label">Feature Management</div>
              <div className="value-circle">
                <div className="value">{userListHID.length}</div>
                <div
                  className="circle-out"
                  style={{ backgroundColor: "#94BB20" }}
                >
                  <div
                    className="circle-in"
                    style={{ backgroundColor: "#94BB20" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
 
      <div className="users-container">
        {/* <div className="header">
          <Logo theme={LogoThemes.Light} />
        </div> */}
        {status.userListHIDLoading ? (
          <div className="loading">
            <Loader variant={LoaderSizes.large} />
          </div>
        ) : userListHID.length > 0 ? (
          <div className="user-list-container">
            <div className="feature-tabs">
              <button
                type="button"
                className={
                  activeTab === "latest" ? "tab-button active" : "tab-button"
                }
                onClick={() => {
                  setActiveTab("latest");
                  setCurrentPageNumber(1);
                }}
              >
                Latest
              </button>
              <button
                type="button"
                className={
                  activeTab === "trending" ? "tab-button active" : "tab-button"
                }
                onClick={() => {
                  setActiveTab("trending");
                  setCurrentPageNumber(1);
                }}
              >
                Trending
              </button>
            </div>
            <div className="search-add-wrapper">
              {/* <div className="search-input-wrapper">
                <SearchInput
                  theme="dark"
                  id="searchUser"
                  name="searchUser"
                  placeholder="Search by Name or Email"
                  isDisplayClearIcon={true}
                  value={searchTerm}
                  onChange={(event: any) =>
                    setSearchTerm(event.currentTarget.value)
                  }
                  onSearch={(event, value) => {
                    setSearchTerm(value);
                  }}
                />
              </div> */}
              <div className="add-new-user-button-wrapper">
                {/* {AddEditUserPermission && ( */}
                <Button
                  variant="primary"
                  label="Add New Feature"
                  icon={
                    <Iconography
                      icon="userPlus"
                      size={IconographySizes.Medium}
                    />
                  }
                  onClick={() => {
                    onAddUserClick();
                  }}
                />
                {/* )} */}
              </div>
            </div>
            <div className="user-list-wrapper">
              {totalRecords > 0 ? (
                <>
                  {paginatedData.map((user) => {
                    return <UserCard user={user} key={user.ID} />;
                  })}
                </>
              ) : (
                <div className="no-users-found">
                  <p>No features found</p>
                </div>
              )}
            </div>
            {totalRecords > 0 && (
              <div className="pagination-wrapper">
                <Pagination
                  currentPageNumber={currentPageNumber}
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  onPageNumberClick={handlePageChange}
                  onPageSizesClick={onPageSizesClick}
                  pageSizes={[
                    { label: "16", value: "16" },
                    { label: "32", value: "32" },
                    { label: "64", value: "64" },
                  ]}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="no-hid-users">
              <div className="no-users-icon-wrapper">
                <NoUserStateSVG />
              </div>
              <p>No Features added</p>
              {/* {AddEditUserPermission && ( */}
              <Button
                variant="primary"
                label="Add New Feature"
                icon={
                  <Iconography icon="userPlus" size={IconographySizes.Medium} />
                }
                onClick={() => {
                  onAddUserClick();
                }}
              />
              {/* )} */}
            </div>
          </>
        )}
        {addModalOpen && (
          <HIDUserFormModal
            setModalOpen={setAddModalOpen}
            mode="Add"
            onSuccess={() => setRefreshKey((prev) => prev + 1)}
          />
        )}
        {updateModalOpen && (
          <HIDUserFormModal
            user={selectedUser}
            setModalOpen={setUpdateModalOpen}
            mode="Update"
            onSuccess={() => setRefreshKey((prev) => prev + 1)}
          />
        )}
        {deleteModalOpen && (
          <DeleteModal
            setDeleteOpen={setDeleteModalOpen}
            onDeleteButtonClick={handleDeleteUser}
            deleteButtonLoading={status.deleteUserLoading}
            optionalHeader="Delete User Confirmation"
            info={{
              icon: "user",
              name: `${userToDelete?.site}`,
            }}
            message={`Are you sure you want to delete feature ? This action cannot be undone.`}
          />
        )}
      </div>
    </div>
  );
};
 
export default FeatureList;